import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import { Button, LinearProgress } from '@material-ui/core';
import { useHistory } from 'react-router';
import { useActor, useInterpret } from '@xstate/react';
import { format } from 'date-fns';
import { Alert, AlertTitle } from '@mui/material';

import { selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import OrganizationMenu from 'retired/OrganizationMenu';
import { ModalTypography } from 'components/Modal/Modal';
import { getAllowedSteps } from 'components/Flow/flows/getAllowedSteps';
import { verificationMachine } from 'components/Flow/flows/nonprofitOnboardingVerification/machine';
import { getVerificationMachineContext } from 'components/Flow/flows/nonprofitOnboardingVerification/getVerificationMachineContext';
import { UserState } from 'entities/user/reducer';
import { NonprofitOnboardingFlowContext } from 'components/Flow/flows/nonprofitOnboarding/context';
import { NonprofitOnboardingVerification } from 'components/Flow/flows/nonprofitOnboardingVerification';

import { Privileges } from '../../utils/privileges';

// @NOTE-DP: in order to figure if PayPal verification is enabled, we run through the state machine and assert the state it arrives at
export const usePayPalVerificationState = (user: UserState) => {
  const service = useInterpret(verificationMachine, {
    context: getVerificationMachineContext(
      // @NOTE-DP: okay, this is a bit funky. We want to show the verification tab even to those who are already verified, so we need to filter out `ORGANIZATION_SELF_VERIFIED` privilege
      // @ts-expect-error immutable is not typed
      user.set(
        'privileges',
        user?.privileges.filter((privilege) => privilege !== Privileges.ORGANIZATION_SELF_VERIFIED)
      ),
      undefined,
      () => ({ privileges: [], nonprofit: {} })
    ),
    actions: {} as any,
  });
  const [state] = useActor(service);

  const isCompany = user?.organization?.isCompany();
  if (isCompany) {
    return null;
  }

  return state;
};

export const usePayPalVerificationEnabled = (user: UserState) => {
  const state = usePayPalVerificationState(user);

  if (!getAllowedSteps(user).includes('verification')) {
    return false;
  }
  return (state?.value as { paypal: string })?.paypal === 'welcome';
};

const VerificationRouteInner = ({ user }: { user: UserState }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [id, setId] = useState(0);
  const history = useHistory();
  const payPalVerificationEnabled = usePayPalVerificationEnabled(user);

  if (!payPalVerificationEnabled) {
    history.replace('/');
  }

  const verifiedRoleId = user?.roles.find?.((role) => role.code === 'Verified')?._id;
  const verifiedRoleGrant = user?.roleGrants.find(
    (roleGrant) =>
      roleGrant.enabled &&
      roleGrant.context?.organization?.id === user.organization.id &&
      roleGrant.role === verifiedRoleId
  );
  const verifiedAt = verifiedRoleGrant?.metadata?.assignedAt;

  const title = 'User Verification with PayPal';

  return (
    <>
      <Helmet title={title} />
      {(user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) ||
        user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)) && (
        <div style={{ marginBottom: 60 }}>
          <OrganizationMenu user={user} />
        </div>
      )}
      {!user && (
        <div style={{ maxWidth: 1000 }}>
          <LinearProgress />
        </div>
      )}
      <Box style={{ maxWidth: 1000 }}>
        <h2>{title}</h2>

        {verifiedAt ? (
          <Alert>
            <AlertTitle>Successful verification</AlertTitle>
            You have successfully verified with PayPal on{' '}
            {format(new Date(verifiedAt), 'MMM dd, yyyy')}
          </Alert>
        ) : (
          <>
            <div style={{ maxWidth: 700 }}>
              <ModalTypography>
                In order to become an Owner on Deed, we kindly ask you to undergo verification via
                PayPal. Clicking the verification link will redirect you to the PayPal website,
                where you’ll need to enter the account owner’s credentials. Please note that users
                can only verify themselves using the account owner credentials for a PayPal business
                account of your organization.
              </ModalTypography>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                Verify with PayPal
              </Button>
            </div>
          </>
        )}
      </Box>
      {modalOpen && (
        <NonprofitOnboardingFlowContext.Provider
          value={{
            isTransitionAllowed: (action) => action === 'DISMISS',
            send: (action) => {
              if (action === 'DISMISS') {
                setModalOpen(false);
                setId(id + 1);
              }
            },
            state: {},
          }}
        >
          <NonprofitOnboardingVerification
            send={(action) => {
              if (action === 'NEXT') {
                setModalOpen(false);
                setId(id + 1);
              }
            }}
            isTransitionAllowed={() => false}
            id={String(id)}
            standaloneMode
          />
        </NonprofitOnboardingFlowContext.Provider>
      )}
    </>
  );
};

const VerificationRoute = () => {
  const user = useSelector(selectUser);

  if (!user) {
    return null;
  }

  return <VerificationRouteInner user={user} />;
};

export default VerificationRoute;
